'use client';

import { useState } from 'react';
import Link from 'next/link';
import Navbar from '../../../components/Navbar';
import Footer from '../../../components/Footer';

// Tools data
const tools = [
    {
        id: 'emi-calculator',
        name: 'EMI Calculator',
        description: 'Calculate your monthly EMI for home loans',
        icon: '🧮',
    },
    {
        id: 'affordability-calculator',
        name: 'Affordability Calculator',
        description: 'Find out how much home you can afford',
        icon: '💰',
    },
    {
        id: 'area-converter',
        name: 'Area Converter',
        description: 'Convert between sqft, sqmt, and other units',
        icon: '📐',
    },
    {
        id: 'stamp-duty-calculator',
        name: 'Stamp Duty Calculator',
        description: 'Calculate stamp duty and registration charges',
        icon: '📄',
    },
];

export default function ToolsPage() {
    const [loanAmount, setLoanAmount] = useState(5000000);
    const [interestRate, setInterestRate] = useState(8.5);
    const [tenure, setTenure] = useState(20);

    // EMI Calculation
    const calculateEMI = () => {
        const principal = loanAmount;
        const rate = interestRate / 12 / 100;
        const months = tenure * 12;

        const emi = (principal * rate * Math.pow(1 + rate, months)) / (Math.pow(1 + rate, months) - 1);
        return Math.round(emi);
    };

    const emi = calculateEMI();
    const totalAmount = emi * tenure * 12;
    const totalInterest = totalAmount - loanAmount;

    const formatCurrency = (num) => {
        if (num >= 10000000) return `₹${(num / 10000000).toFixed(2)} Cr`;
        if (num >= 100000) return `₹${(num / 100000).toFixed(2)} Lac`;
        return `₹${num.toLocaleString('en-IN')}`;
    };

    return (
        <>
            <Navbar />
            <main className="min-h-screen bg-gray-50">
                {/* Header */}
                <section className="bg-white py-4">
                    <div className="container-custom">
                        <nav className="flex items-center gap-2 text-sm text-gray-500">
                            <Link href="/" className="hover:text-primary">Home</Link>
                            <span>→</span>
                            <span className="text-gray-900"> EMI Calculator </span>
                        </nav>
                    </div>
                </section>

                <div className="container-custom">
                    <div className="grid lg:grid-cols-3 gap-8 py-6">
                        {/* EMI Calculator (Main Tool) */}
                        <div className="lg:col-span-2">
                            <div className="bg-white rounded-xl p-6 lg:p-8 shadow-sm">
                                <div className="flex items-center gap-3 mb-6">
                                    <span className="text-3xl">🧮</span>
                                    <div>
                                        <h2 className="text-xl font-bold text-gray-900">EMI Calculator</h2>
                                        <p className="text-gray-500 text-sm">Calculate your monthly home loan EMI</p>
                                    </div>
                                </div>

                                {/* Loan Amount */}
                                <div className="mb-6">
                                    <div className="flex justify-between mb-2">
                                        <label className="text-sm font-medium text-gray-700">Loan Amount</label>
                                        <span className="text-sm font-bold text-primary">{formatCurrency(loanAmount)}</span>
                                    </div>
                                    <input
                                        type="range"
                                        min="500000"
                                        max="100000000"
                                        step="100000"
                                        value={loanAmount}
                                        onChange={(e) => setLoanAmount(Number(e.target.value))}
                                        className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-primary"
                                    />
                                    <div className="flex justify-between text-xs text-gray-400 mt-1">
                                        <span>₹5L</span>
                                        <span>₹10Cr</span>
                                    </div>
                                </div>

                                {/* Interest Rate */}
                                <div className="mb-6">
                                    <div className="flex justify-between mb-2">
                                        <label className="text-sm font-medium text-gray-700">Interest Rate</label>
                                        <span className="text-sm font-bold text-primary">{interestRate}%</span>
                                    </div>
                                    <input
                                        type="range"
                                        min="5"
                                        max="15"
                                        step="0.1"
                                        value={interestRate}
                                        onChange={(e) => setInterestRate(Number(e.target.value))}
                                        className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-primary"
                                    />
                                    <div className="flex justify-between text-xs text-gray-400 mt-1">
                                        <span>5%</span>
                                        <span>15%</span>
                                    </div>
                                </div>

                                {/* Tenure */}
                                <div className="mb-8">
                                    <div className="flex justify-between mb-2">
                                        <label className="text-sm font-medium text-gray-700">Loan Tenure</label>
                                        <span className="text-sm font-bold text-primary">{tenure} years</span>
                                    </div>
                                    <input
                                        type="range"
                                        min="1"
                                        max="30"
                                        step="1"
                                        value={tenure}
                                        onChange={(e) => setTenure(Number(e.target.value))}
                                        className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-primary"
                                    />
                                    <div className="flex justify-between text-xs text-gray-400 mt-1">
                                        <span>1 year</span>
                                        <span>30 years</span>
                                    </div>
                                </div>

                                {/* Results */}
                                <div className="grid grid-cols-3 gap-4 p-6 bg-gray-50 rounded-xl">
                                    <div className="text-center">
                                        <div className="text-2xl lg:text-3xl font-bold text-primary">
                                            ₹{emi.toLocaleString('en-IN')}
                                        </div>
                                        <div className="text-sm text-gray-500 mt-1">Monthly EMI</div>
                                    </div>
                                    <div className="text-center border-x border-gray-200">
                                        <div className="text-2xl lg:text-3xl font-bold text-gray-900">
                                            {formatCurrency(totalInterest)}
                                        </div>
                                        <div className="text-sm text-gray-500 mt-1">Total Interest</div>
                                    </div>
                                    <div className="text-center">
                                        <div className="text-2xl lg:text-3xl font-bold text-gray-900">
                                            {formatCurrency(totalAmount)}
                                        </div>
                                        <div className="text-sm text-gray-500 mt-1">Total Amount</div>
                                    </div>
                                </div>

                                <div className="mt-6">
                                    <Link href="/services/home-loans" className="btn btn-primary w-full">
                                        Check Home Loan Offers
                                    </Link>
                                </div>
                            </div>
                        </div>

                        {/* Other Tools */}
                        <div className="space-y-4">
                            <h3 className="text-lg font-bold text-gray-900">More Tools</h3>
                            {tools.slice(1).map((tool) => (
                                <Link
                                    key={tool.id}
                                    href={`/tools/${tool.id}`}
                                    className="block p-4 bg-white rounded-xl shadow-sm hover:shadow-md transition-all"
                                >
                                    <div className="flex items-center gap-3">
                                        <span className="text-2xl">{tool.icon}</span>
                                        <div>
                                            <h4 className="font-semibold text-gray-900">{tool.name}</h4>
                                            <p className="text-sm text-gray-500">{tool.description}</p>
                                        </div>
                                    </div>
                                </Link>
                            ))}

                            {/* Tip Card */}
                            <div className="p-5 bg-primary/5 border border-primary/20 rounded-xl">
                                <h4 className="font-semibold text-gray-900 mb-2">💡 Pro Tip</h4>
                                <p className="text-sm text-gray-600">
                                    A lower interest rate and longer tenure can reduce your monthly EMI,
                                    but you'll pay more interest overall.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </main>
            <Footer />
        </>
    );
}
